import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Heart, HandHeart, Moon, Home, Users, Shield, CheckCircle, Loader2, Download, Mail, FileText, CreditCard } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { DONATE_FAQ } from '../data/donateFaq';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DonatePage = () => {
  const { t, language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [donationType, setDonationType] = useState('single');
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [donationSuccess, setDonationSuccess] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [isVerifying, setIsVerifying] = useState(false);
  const [formData, setFormData] = useState({
    donor_name: '',
    email: '',
    amount: '',
    message: '',
    is_anonymous: false,
  });
  
  // Use refs instead of state to prevent re-renders causing double execution
  const isProcessingRef = useRef(false);
  const hasProcessedPaymentRef = useRef(false);

  const amounts = [50, 75, 100, 250, 500, 1000];

  // Check for PayPal/Stripe return - runs only once on mount
  useEffect(() => {
    const stripeSessionId = searchParams.get('stripe_session_id');
    const stripeCancelled = searchParams.get('stripe_cancelled');

    if (stripeCancelled === 'true') {
      toast.error(language === 'fr' ? 'Paiement annulé' : 'Payment cancelled');
      setSearchParams({}, { replace: true });
      return;
    }

    if (stripeSessionId) {
      if (hasProcessedPaymentRef.current) return;
      hasProcessedPaymentRef.current = true;
      setSearchParams({}, { replace: true });
      setIsVerifying(true);

      const pollStripeStatus = async () => {
        const pending = JSON.parse(localStorage.getItem('pendingStripeDonation') || '{}');
        for (let i = 0; i < 12; i++) {
          try {
            const res = await axios.get(`${API}/stripe/status/${stripeSessionId}`);
            if (res.data.payment_status === 'paid') {
              toast.success(language === 'fr' ? 'Merci pour votre don!' : 'Thank you for your donation!');
              setDonationSuccess({
                donationId: res.data.donation_id,
                receiptNumber: res.data.receipt_number,
                receiptGenerated: res.data.receipt_generated || false,
                amount: res.data.amount ?? parseFloat(pending.amount),
                donorName: pending.donor_name,
                email: pending.email
              });
              localStorage.removeItem('pendingStripeDonation');
              setIsVerifying(false);
              return;
            }
            if (res.data.payment_status === 'failed' || res.data.payment_status === 'expired') {
              toast.error(language === 'fr' ? 'Le paiement n\'a pas pu être complété.' : 'The payment could not be completed.');
              setIsVerifying(false);
              return;
            }
          } catch (e) {
            // retry on next tick
          }
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
        setIsVerifying(false);
        toast.error(language === 'fr'
          ? 'La vérification prend plus de temps que prévu. Vérifiez votre courriel pour la confirmation.'
          : 'Verification is taking longer than expected. Please check your email for confirmation.');
      };

      pollStripeStatus();
      return;
    }

    const processPaypalReturn = async () => {
      const success = searchParams.get('success');
      const cancelled = searchParams.get('cancelled');
      const paymentId = searchParams.get('paymentId');
      const payerId = searchParams.get('PayerID');

      // Handle cancelled payment
      if (cancelled === 'true') {
        toast.error(language === 'fr' ? 'Paiement annulé' : 'Payment cancelled');
        setSearchParams({}, { replace: true });
        return;
      }

      // Check if we should process a payment
      if (success !== 'true' || !paymentId || !payerId) {
        return;
      }

      // CRITICAL: Check refs to prevent double execution (React StrictMode)
      if (isProcessingRef.current || hasProcessedPaymentRef.current) {
        return;
      }

      // Mark as processing IMMEDIATELY before any async work
      isProcessingRef.current = true;
      hasProcessedPaymentRef.current = true;

      // Clear URL params IMMEDIATELY to prevent re-execution on any re-render
      setSearchParams({}, { replace: true });

      try {
        // Get stored donation data
        const pendingDonation = JSON.parse(localStorage.getItem('pendingDonation') || '{}');
        
        // Execute the payment
        const executeResponse = await axios.get(`${API}/paypal/execute`, {
          params: { paymentId, PayerID: payerId }
        });
        
        // Both normal success and already_processed are SUCCESS cases
        if (executeResponse.data.success) {
          toast.success(language === 'fr' ? 'Merci pour votre don!' : 'Thank you for your donation!');
          setDonationSuccess({
            donationId: executeResponse.data.donation_id || 'processed',
            receiptNumber: executeResponse.data.receipt_number,
            receiptGenerated: executeResponse.data.receipt_generated || false,
            amount: parseFloat(pendingDonation.amount),
            donorName: pendingDonation.donor_name,
            email: pendingDonation.email
          });
          localStorage.removeItem('pendingDonation');
          
          // If not already processed, also capture the order with full data
          if (!executeResponse.data.already_processed) {
            try {
              const captureResponse = await axios.post(`${API}/paypal/capture-order`, {
                order_id: paymentId,
                donor_name: pendingDonation.donor_name || 'Donateur',
                email: pendingDonation.email || '',
                amount: parseFloat(pendingDonation.amount) || 0,
                donation_type: pendingDonation.donation_type || 'single',
                message: pendingDonation.message || '',
                is_anonymous: pendingDonation.is_anonymous || false
              });
              
              if (captureResponse.data.success) {
                // Update success state with more complete data from capture
                setDonationSuccess({
                  donationId: captureResponse.data.donation_id,
                  receiptNumber: captureResponse.data.receipt_number,
                  receiptGenerated: captureResponse.data.receipt_generated,
                  amount: parseFloat(pendingDonation.amount),
                  donorName: pendingDonation.donor_name,
                  email: pendingDonation.email
                });
              }
            } catch (captureError) {
              // Capture failed but execute succeeded - still a success for user
              // Error logged silently - user already sees success
            }
          }
          return;
        }
      } catch (error) {
        // Check if it's a "payment already done" error - this means SUCCESS
        const errorDetail = error.response?.data?.detail || '';
        const errorMessage = error.message || '';
        
        if (errorDetail.includes('PAYMENT_ALREADY_DONE') || 
            errorDetail.includes('already processed') ||
            errorMessage.includes('PAYMENT_ALREADY_DONE')) {
          const pendingDonation = JSON.parse(localStorage.getItem('pendingDonation') || '{}');
          toast.success(language === 'fr' ? 'Merci pour votre don!' : 'Thank you for your donation!');
          setDonationSuccess({
            donationId: error.response?.data?.donation_id || 'processed',
            receiptNumber: error.response?.data?.receipt_number,
            receiptGenerated: error.response?.data?.receipt_generated || false,
            amount: parseFloat(pendingDonation.amount),
            donorName: pendingDonation.donor_name,
            email: pendingDonation.email
          });
          localStorage.removeItem('pendingDonation');
          return;
        }
        
        // Only show error for genuine failures
        toast.error(language === 'fr' ? 'Une erreur est survenue. Veuillez vérifier votre courriel pour la confirmation.' : 'An error occurred. Please check your email for confirmation.');
      } finally {
        isProcessingRef.current = false;
      }
    };

    processPaypalReturn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - run only once on mount

  const handleDownloadReceipt = async () => {
    if (!donationSuccess?.donationId) return;
    
    try {
      const response = await axios.get(`${API}/receipt/${donationSuccess.donationId}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Recu_Fiscal_${donationSuccess.receiptNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(language === 'fr' ? 'Reçu téléchargé!' : 'Receipt downloaded!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors du téléchargement' : 'Download error');
    }
  };

  const handleResendReceipt = async () => {
    if (!donationSuccess?.donationId) return;
    
    try {
      await axios.post(`${API}/receipt/resend/${donationSuccess.donationId}`);
      toast.success(language === 'fr' ? 'Reçu envoyé par courriel!' : 'Receipt sent by email!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de l\'envoi' : 'Send error');
    }
  };

  const handleNewDonation = () => {
    setDonationSuccess(null);
    setFormData({
      donor_name: '',
      email: '',
      amount: '',
      message: '',
      is_anonymous: false,
    });
    setSelectedAmount(null);
  };

  const impactItems = [
    { id: 'welcome', icon: HandHeart, text: t.donate.impact1, amount: 50 },
    { id: 'shelter-night', icon: Moon, text: t.donate.impact2, amount: 75 },
    { id: 'shelter-meals', icon: Home, text: t.donate.impact3, amount: 100 },
    { id: 'reconstruction', icon: Heart, text: t.donate.impact4, amount: 500 },
    { id: 'full-support', icon: Users, text: t.donate.impact5, amount: 1000 },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'amount') {
      setSelectedAmount(null);
    }
  };

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setFormData((prev) => ({ ...prev, amount: amount.toString() }));
  };

  const handlePayPalPayment = async (e) => {
    e.preventDefault();
    
    const amount = parseFloat(formData.amount);
    
    if (!formData.amount || isNaN(amount) || amount < 1) {
      toast.error(language === 'fr' ? 'Veuillez entrer un montant valide (minimum 1$)' : 'Please enter a valid amount (minimum $1)');
      return;
    }
    
    if (!formData.donor_name || !formData.email) {
      toast.error(language === 'fr' ? 'Veuillez remplir tous les champs obligatoires' : 'Please fill all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const requestData = {
        amount: amount,
        donation_type: donationType,
        donor_name: formData.donor_name,
        email: formData.email,
        message: formData.message || '',
        is_anonymous: formData.is_anonymous || false,
      };

      if (paymentMethod === 'card') {
        const response = await axios.post(`${API}/stripe/checkout`, {
          ...requestData,
          origin_url: window.location.origin,
        });
        if (response.data.checkout_url) {
          localStorage.setItem('pendingStripeDonation', JSON.stringify({
            ...formData,
            donation_type: donationType,
          }));
          window.location.href = response.data.checkout_url;
        } else {
          toast.error(t.common.error);
        }
        return;
      }

      const response = await axios.post(`${API}/paypal/create-order`, requestData);

      if (response.data.success && response.data.approval_url) {
        // Store form data in localStorage for after redirect
        localStorage.setItem('pendingDonation', JSON.stringify({
          ...formData,
          donation_type: donationType,
          payment_id: response.data.payment_id
        }));
        
        // Redirect to PayPal
        window.location.href = response.data.approval_url;
      } else {
        toast.error(t.common.error);
      }
    } catch (error) {
      let errorMessage = t.common.error;
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail);
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="donate-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-b from-[#D4AF37]/10 to-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="w-20 h-20 mx-auto rounded-full bg-[#D4AF37]/20 flex items-center justify-center mb-8">
              <Heart className="w-10 h-10 text-[#D4AF37]" />
            </div>
            <h1 
              data-testid="donate-title"
              className="font-serif text-4xl sm:text-5xl font-bold text-[#1A252C] mb-6"
            >
              {t.donate.pageTitle}
            </h1>
            <p className="text-xl text-[#546A76] leading-relaxed">
              {t.donate.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Left - Impact */}
            <div>
              <h2 className="font-serif text-3xl font-bold text-[#1A252C] mb-8">
                {t.donate.impactTitle}
              </h2>
              
              <div className="space-y-4">
                {impactItems.map((item) => (
                  <div 
                    key={item.id}
                    data-testid={`donation-impact-${item.id}`}
                    onClick={() => handleAmountSelect(item.amount)}
                    className={`flex items-start gap-4 p-5 rounded-2xl border-2 cursor-pointer transition-all ${
                      selectedAmount === item.amount 
                        ? 'border-[#D4AF37] bg-[#D4AF37]/5' 
                        : 'border-[#E2E8F0] hover:border-[#D4AF37]/50'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      selectedAmount === item.amount 
                        ? 'bg-[#D4AF37]' 
                        : 'bg-[#D4AF37]/10'
                    }`}>
                      <item.icon className={`w-6 h-6 ${
                        selectedAmount === item.amount 
                          ? 'text-white' 
                          : 'text-[#D4AF37]'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <div className={`text-xl font-bold mb-1 ${
                        selectedAmount === item.amount 
                          ? 'text-[#D4AF37]' 
                          : 'text-[#1A252C]'
                      }`}>
                        {item.amount} $
                      </div>
                      <p className="text-[#546A76] text-sm leading-relaxed">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Transparency Section */}
              <div className="mt-12 bg-[#4A708B] rounded-3xl p-8">
                <div className="flex items-start gap-4">
                  <Shield className="w-8 h-8 text-white flex-shrink-0" />
                  <div>
                    <h3 className="font-serif text-xl font-semibold text-white mb-3">
                      {t.donate.transparencyTitle}
                    </h3>
                    <p className="text-white/90 leading-relaxed">
                      {t.donate.transparencyText}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Form */}
            <div>
              <div className="bg-[#FAFAFA] rounded-3xl p-8 sm:p-10">
                {/* Success State */}
                {donationSuccess ? (
                  <div className="text-center py-8" data-testid="donation-success">
                    <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-6">
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <h2 className="font-serif text-2xl font-bold text-[#1A252C] mb-4">
                      {language === 'fr' ? 'Merci pour votre don!' : 'Thank you for your donation!'}
                    </h2>
                    <p className="text-[#546A76] mb-2">
                      {language === 'fr' 
                        ? `Votre don de ${donationSuccess.amount?.toFixed(2)}$ CAD a été reçu avec succès.`
                        : `Your donation of $${donationSuccess.amount?.toFixed(2)} CAD has been received successfully.`}
                    </p>
                    <p className="text-[#546A76] mb-8">
                      {language === 'fr'
                        ? 'Un courriel de confirmation vous a été envoyé.'
                        : 'A confirmation email has been sent to you.'}
                    </p>

                    {/* Tax Receipt Section */}
                    {donationSuccess.receiptGenerated && (
                      <div className="bg-white rounded-2xl p-6 mb-6 border border-[#E2E8F0]">
                        <div className="flex items-center justify-center gap-2 mb-4">
                          <FileText className="w-5 h-5 text-[#4A708B]" />
                          <h3 className="font-semibold text-[#1A252C]">
                            {language === 'fr' ? 'Reçu fiscal disponible' : 'Tax receipt available'}
                          </h3>
                        </div>
                        <p className="text-sm text-[#546A76] mb-4">
                          {language === 'fr'
                            ? `Numéro du reçu: ${donationSuccess.receiptNumber}`
                            : `Receipt number: ${donationSuccess.receiptNumber}`}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                          <Button
                            onClick={handleDownloadReceipt}
                            data-testid="download-receipt-btn"
                            className="primary-btn rounded-full"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            {language === 'fr' ? 'Télécharger le reçu' : 'Download receipt'}
                          </Button>
                          <Button
                            onClick={handleResendReceipt}
                            variant="outline"
                            data-testid="resend-receipt-btn"
                            className="rounded-full border-[#4A708B] text-[#4A708B] hover:bg-[#4A708B]/10"
                          >
                            <Mail className="w-4 h-4 mr-2" />
                            {language === 'fr' ? 'Renvoyer par courriel' : 'Resend by email'}
                          </Button>
                        </div>
                      </div>
                    )}

                    {!donationSuccess.receiptGenerated && donationSuccess.amount < 25 && (
                      <div className="bg-amber-50 rounded-2xl p-4 mb-6 border border-amber-200">
                        <p className="text-sm text-amber-800">
                          {language === 'fr'
                            ? 'Note: Les reçus fiscaux sont émis pour les dons de 25$ et plus.'
                            : 'Note: Tax receipts are issued for donations of $25 and above.'}
                        </p>
                      </div>
                    )}

                    <Button
                      onClick={handleNewDonation}
                      variant="outline"
                      data-testid="new-donation-btn"
                      className="rounded-full border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
                    >
                      <Heart className="w-4 h-4 mr-2" />
                      {language === 'fr' ? 'Faire un autre don' : 'Make another donation'}
                    </Button>
                  </div>
                ) : isVerifying ? (
                  <div className="text-center py-16" data-testid="stripe-verifying">
                    <Loader2 className="w-12 h-12 mx-auto animate-spin text-[#D4AF37] mb-6" />
                    <h2 className="font-serif text-2xl font-bold text-[#1A252C] mb-3">
                      {language === 'fr' ? 'Vérification du paiement...' : 'Verifying payment...'}
                    </h2>
                    <p className="text-[#546A76]">
                      {language === 'fr' ? 'Un instant, nous confirmons votre don.' : 'One moment, we are confirming your donation.'}
                    </p>
                  </div>
                ) : (
                  <>
                {/* Donation Type Toggle */}
                <div className="flex rounded-full bg-white p-1 mb-8 border border-[#E2E8F0]">
                  <button
                    onClick={() => setDonationType('single')}
                    data-testid="donation-type-single"
                    className={`flex-1 py-3 px-6 rounded-full font-medium transition-all ${
                      donationType === 'single'
                        ? 'bg-[#D4AF37] text-white'
                        : 'text-[#546A76] hover:text-[#1A252C]'
                    }`}
                  >
                    {t.donate.singleDonation}
                  </button>
                  <button
                    onClick={() => setDonationType('monthly')}
                    data-testid="donation-type-monthly"
                    className={`flex-1 py-3 px-6 rounded-full font-medium transition-all ${
                      donationType === 'monthly'
                        ? 'bg-[#D4AF37] text-white'
                        : 'text-[#546A76] hover:text-[#1A252C]'
                    }`}
                  >
                    {t.donate.monthlyDonation}
                  </button>
                </div>

                {/* Amount Selection */}
                <div className="mb-8">
                  <Label className="mb-4 block">{t.donate.amount}</Label>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    {amounts.map((amount) => (
                      <button
                        key={amount}
                        onClick={() => handleAmountSelect(amount)}
                        data-testid={`amount-${amount}`}
                        className={`py-4 rounded-xl font-semibold transition-all ${
                          selectedAmount === amount
                            ? 'bg-[#D4AF37] text-white'
                            : 'bg-white border-2 border-[#E2E8F0] text-[#1A252C] hover:border-[#D4AF37]'
                        }`}
                      >
                        ${amount}
                      </button>
                    ))}
                  </div>
                  <Input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    placeholder={t.donate.amount}
                    min="1"
                    data-testid="donation-amount-input"
                    className="rounded-xl bg-white border-[#E2E8F0] focus:border-[#4A708B]"
                  />
                </div>

                <form onSubmit={handlePayPalPayment} data-testid="donation-form" className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="donor_name">{t.donate.name} *</Label>
                    <Input
                      id="donor_name"
                      name="donor_name"
                      value={formData.donor_name}
                      onChange={handleChange}
                      required
                      data-testid="donation-name"
                      className="rounded-xl bg-white border-[#E2E8F0] focus:border-[#4A708B]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">{t.donate.email} *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="donation-email"
                      className="rounded-xl bg-white border-[#E2E8F0] focus:border-[#4A708B]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">{t.donate.message}</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={3}
                      data-testid="donation-message"
                      className="rounded-xl bg-white border-[#E2E8F0] focus:border-[#4A708B]"
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="is_anonymous"
                      checked={formData.is_anonymous}
                      onCheckedChange={(checked) => 
                        setFormData((prev) => ({ ...prev, is_anonymous: checked }))
                      }
                      data-testid="donation-anonymous"
                    />
                    <Label htmlFor="is_anonymous" className="cursor-pointer">
                      {t.donate.anonymous}
                    </Label>
                  </div>

                  {/* Payment Method Selector */}
                  <div className="space-y-3">
                    <Label>{language === 'fr' ? 'Méthode de paiement' : 'Payment method'}</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('card')}
                        data-testid="payment-method-card"
                        className={`flex items-center justify-center gap-2 py-4 rounded-xl font-medium transition-all border-2 ${
                          paymentMethod === 'card'
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#1A252C]'
                            : 'border-[#E2E8F0] bg-white text-[#546A76] hover:border-[#D4AF37]/50'
                        }`}
                      >
                        <CreditCard className={`w-5 h-5 ${paymentMethod === 'card' ? 'text-[#D4AF37]' : ''}`} />
                        {language === 'fr' ? 'Carte bancaire' : 'Credit card'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('paypal')}
                        data-testid="payment-method-paypal"
                        className={`flex items-center justify-center gap-2 py-4 rounded-xl font-medium transition-all border-2 ${
                          paymentMethod === 'paypal'
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#1A252C]'
                            : 'border-[#E2E8F0] bg-white text-[#546A76] hover:border-[#D4AF37]/50'
                        }`}
                      >
                        <svg className={`w-5 h-5 ${paymentMethod === 'paypal' ? 'text-[#003087]' : ''}`} viewBox="0 0 24 24" fill="currentColor">
                          <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.643h6.96c2.314 0 4.023.587 5.082 1.744.473.52.792 1.106.948 1.742.162.659.184 1.447.065 2.34-.072.533-.192 1.03-.36 1.488-.168.458-.387.889-.652 1.29-.265.4-.588.768-.964 1.098-.377.33-.816.623-1.31.872-.495.25-1.058.455-1.677.61-.62.155-1.31.235-2.063.235h-1.02a.96.96 0 0 0-.946.806l-.028.164-.697 4.4-.023.12a.96.96 0 0 1-.946.806H7.076z"/>
                        </svg>
                        PayPal
                      </button>
                    </div>
                  </div>

                  {/* Payment Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting || !formData.amount}
                    data-testid="paypal-donate-btn"
                    className="w-full gold-btn rounded-full py-6 text-lg font-medium"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                        {language === 'fr' ? 'Redirection...' : 'Redirecting...'}
                      </>
                    ) : paymentMethod === 'card' ? (
                      <>
                        <CreditCard className="w-5 h-5 mr-2" />
                        {language === 'fr' ? 'Payer par carte' : 'Pay by card'}
                      </>
                    ) : (
                      <>
                        <svg className="w-6 h-6 mr-2" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.643h6.96c2.314 0 4.023.587 5.082 1.744.473.52.792 1.106.948 1.742.162.659.184 1.447.065 2.34-.072.533-.192 1.03-.36 1.488-.168.458-.387.889-.652 1.29-.265.4-.588.768-.964 1.098-.377.33-.816.623-1.31.872-.495.25-1.058.455-1.677.61-.62.155-1.31.235-2.063.235h-1.02a.96.96 0 0 0-.946.806l-.028.164-.697 4.4-.023.12a.96.96 0 0 1-.946.806H7.076zM19.167 7.27l-.025.148c-.732 3.76-3.237 5.06-6.44 5.06h-1.628a.79.79 0 0 0-.78.666l-.98 6.2-.277 1.753a.416.416 0 0 0 .41.48h2.877a.692.692 0 0 0 .683-.584l.028-.147.543-3.44.035-.19a.692.692 0 0 1 .683-.584h.43c2.783 0 4.963-1.13 5.602-4.4.267-1.368.128-2.51-.578-3.312a2.73 2.73 0 0 0-.583-.65z"/>
                        </svg>
                        {language === 'fr' ? 'Payer avec PayPal' : 'Pay with PayPal'}
                      </>
                    )}
                  </Button>

                  {/* Payment Methods Info */}
                  <div className="bg-[#F8FAFC] rounded-xl p-4 border border-[#E2E8F0]">
                    <p className="text-sm text-[#546A76] text-center mb-3">
                      {language === 'fr' 
                        ? 'Modes de paiement acceptés' 
                        : 'Accepted payment methods'}
                    </p>
                    <div className="flex items-center justify-center gap-3 flex-wrap">
                      {/* PayPal */}
                      <div className="flex items-center gap-1 bg-white px-3 py-1.5 rounded-lg border border-[#E2E8F0]">
                        <svg className="w-5 h-5 text-[#003087]" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.643h6.96c2.314 0 4.023.587 5.082 1.744.473.52.792 1.106.948 1.742.162.659.184 1.447.065 2.34-.072.533-.192 1.03-.36 1.488-.168.458-.387.889-.652 1.29-.265.4-.588.768-.964 1.098-.377.33-.816.623-1.31.872-.495.25-1.058.455-1.677.61-.62.155-1.31.235-2.063.235h-1.02a.96.96 0 0 0-.946.806l-.028.164-.697 4.4-.023.12a.96.96 0 0 1-.946.806H7.076z"/>
                        </svg>
                        <span className="text-xs font-medium text-[#003087]">PayPal</span>
                      </div>
                      {/* Visa */}
                      <div className="flex items-center gap-1 bg-white px-3 py-1.5 rounded-lg border border-[#E2E8F0]">
                        <svg className="w-8 h-5" viewBox="0 0 48 32" fill="none">
                          <rect width="48" height="32" rx="4" fill="#1A1F71"/>
                          <path d="M19.5 21H17L18.8 11H21.3L19.5 21ZM15.2 11L12.8 18.1L12.5 16.6L11.6 12C11.6 12 11.5 11 10.2 11H6.1L6 11.2C6 11.2 7.5 11.5 9.2 12.5L11.4 21H14L18 11H15.2ZM35.5 21H38L35.9 11H33.8C32.7 11 32.4 11.9 32.4 11.9L28.4 21H31.2L31.8 19.3H35.2L35.5 21ZM32.6 17L34 13.2L34.8 17H32.6ZM28.9 13.8L29.3 11.3C29.3 11.3 28 10.8 26.6 10.8C25.1 10.8 21.7 11.5 21.7 14.5C21.7 17.3 25.6 17.3 25.6 18.8C25.6 20.3 22.1 19.9 20.9 18.9L20.5 21.5C20.5 21.5 21.8 22.2 23.9 22.2C26 22.2 28.9 21 28.9 18.3C28.9 15.5 25 15.2 25 13.9C25 12.6 27.7 12.8 28.9 13.8Z" fill="white"/>
                        </svg>
                      </div>
                      {/* Mastercard */}
                      <div className="flex items-center gap-1 bg-white px-3 py-1.5 rounded-lg border border-[#E2E8F0]">
                        <svg className="w-8 h-5" viewBox="0 0 48 32" fill="none">
                          <rect width="48" height="32" rx="4" fill="#F5F5F5"/>
                          <circle cx="19" cy="16" r="8" fill="#EB001B"/>
                          <circle cx="29" cy="16" r="8" fill="#F79E1B"/>
                          <path d="M24 9.8C25.9 11.3 27.1 13.5 27.1 16C27.1 18.5 25.9 20.7 24 22.2C22.1 20.7 20.9 18.5 20.9 16C20.9 13.5 22.1 11.3 24 9.8Z" fill="#FF5F00"/>
                        </svg>
                      </div>
                      {/* Amex */}
                      <div className="flex items-center gap-1 bg-white px-3 py-1.5 rounded-lg border border-[#E2E8F0]">
                        <svg className="w-8 h-5" viewBox="0 0 48 32" fill="none">
                          <rect width="48" height="32" rx="4" fill="#016FD0"/>
                          <path d="M8 16.5H10.5L9.25 13.5L8 16.5ZM38 11H35L33 14L31 11H20V12.5L19 11H14L11 18H14.5L15 17H16.5L17 18H24V16.5L24.5 18H27.5L28 16.5V18H38L36 15.5L38 13V11Z" fill="white"/>
                        </svg>
                      </div>
                    </div>
                    <p className="text-xs text-[#94A3B8] text-center mt-3">
                      {language === 'fr' 
                        ? '* Payez par carte bancaire (via Stripe) ou avec votre compte PayPal' 
                        : '* Pay by credit card (via Stripe) or with your PayPal account'}
                    </p>
                  </div>

                  {/* Security Note */}
                  <p className="text-center text-sm text-[#546A76]">
                    <Shield className="w-4 h-4 inline mr-1" />
                    {language === 'fr' 
                      ? 'Paiement 100% sécurisé par Stripe et PayPal. Vos informations sont protégées.'
                      : 'Payment 100% secured by Stripe and PayPal. Your information is protected.'}
                  </p>
                </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white" data-testid="donate-faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1A252C] mb-8 text-center">
            {language === 'fr' ? 'Questions fréquentes sur les dons' : 'Donation FAQ'}
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {DONATE_FAQ.map((item, idx) => (
              <AccordionItem key={`faq-${idx}`} value={`faq-${idx}`} data-testid={`donate-faq-item-${idx}`}>
                <AccordionTrigger className="text-left text-[#1A252C] font-medium">
                  {item.q[language === 'en' ? 'en' : 'fr']}
                </AccordionTrigger>
                <AccordionContent className="text-[#546A76] leading-relaxed">
                  {item.a[language === 'en' ? 'en' : 'fr']}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default DonatePage;
